import { isGroupShape } from '@/lib/groups/group-kind'
import { persistForwardGroupApiFromShapes } from '@/lib/groups/persist-forward-group-api'
import type { StructuralGroupHistoryPersistenceDeps } from './types/structural-group-history-persistence-deps'
import {
  collectRemoveGroupEntries,
  findAddGroupEntryInHistory,
} from './helpers/group-history-entries'
import {
  persistRedoUngroupLikeExplicit,
  persistUndoGroupLikeUngroup,
} from './helpers/persist-ungroup-from-history'

/**
 * After `applyHistoryAction` + `applyHistoryPersistencePlan`, persist kind-7 structural
 * group/ungroup transactions to the group action queue (same routes as forward Group / Ungroup).
 */
export function persistStructuralGroupHistoryIfApplicable(
  deps: StructuralGroupHistoryPersistenceDeps
): void {
  const { action, direction, shapes, textBoxes = [] } = deps

  const txnId =
    action.type === 'compound'
      ? action.historyTxnId
      : 'historyTxnId' in action
        ? action.historyTxnId
        : undefined

  if (!txnId) return

  if (txnId.startsWith('group:')) {
    const groupIdFromTxn = txnId.slice('group:'.length)
    const addEntry = findAddGroupEntryInHistory(action)
    const groupId = addEntry?.shape.id ?? groupIdFromTxn

    if (direction === 'undo') {
      persistUndoGroupLikeUngroup(deps, txnId, groupId)
      return
    }

    const liveGroup = shapes.find((s) => s.id === groupId) ?? addEntry?.shape

    if (!liveGroup || !isGroupShape(liveGroup)) return

    persistForwardGroupApiFromShapes({
      shapes,
      textBoxes,
      group: liveGroup,
      insertIndex: addEntry?.historyInsertIndex,
      historyTxnId: txnId,
      projection: deps.projection,
      markEdited: deps.markEdited,
      enqueueGroup: deps.enqueueGroup,
      enqueueRestructure: deps.enqueueRestructure,
      warn: deps.warn,
    })
    return
  }

  if (txnId.startsWith('ungroup:')) {
    if (direction === 'undo') {
      for (const rm of collectRemoveGroupEntries(action)) {
        const live = shapes.find((s) => s.id === rm.shape.id) ?? rm.shape

        if (!isGroupShape(live)) continue

        persistForwardGroupApiFromShapes({
          shapes,
          textBoxes,
          group: live,
          insertIndex: rm.historyInsertIndex,
          historyTxnId: txnId,
          projection: deps.projection,
          markEdited: deps.markEdited,
          enqueueGroup: deps.enqueueGroup,
          enqueueRestructure: deps.enqueueRestructure,
          warn: deps.warn,
        })
      }

      return
    }

    persistRedoUngroupLikeExplicit(deps, txnId)
  }
}
